import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getTenantInfo } from '../features/tenant/tenantSlice';

function daysLeft(until) {
  const now = new Date();
  const d = new Date(until);
  const diff = d.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('es-AR');
};

const Banner = ({ style, children, onRefresh }) => (
  <div style={{
    padding: '10px 16px',
    textAlign: 'center',
    fontSize: 14,
    ...style,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div>{children}</div>
      {onRefresh && (
        <button onClick={onRefresh} style={{ padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}>Actualizar</button>
      )}
    </div>
  </div>
);

const SubscriptionBanner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenant } = useSelector(s => s.tenant || {});
  const { user } = useSelector(s => s.auth || {});
  const { fiscalConfig, fiscalStatus } = useSelector(s => s.fiscal || {});
  const razonSocial = fiscalConfig?.razonSocial || fiscalStatus?.razonSocial || tenant?.businessName || '';
  const cuit = fiscalConfig?.cuit || fiscalStatus?.cuit || '';
  const tipoResp = fiscalConfig?.tipoResponsable || fiscalStatus?.tipoResponsable || '';

  useEffect(() => {
    if (user) dispatch(getTenantInfo());
  }, [dispatch, user]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && user) dispatch(getTenantInfo());
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [dispatch, user]);

  if (!tenant) return null;
  const { subscriptionStatus, graceUntil, currentPeriodEnd, autoRenew, subscriptionExpiry, subscriptionExpiresAt } = tenant;

  if (subscriptionStatus === 'paused') {
    return (
      <Banner style={{ background: '#fff3cd', color: '#856404' }}>
        Tu suscripción está pausada. Algunas funciones podrían estar limitadas. Contactá al administrador para reactivar.
      </Banner>
    );
  }

  if (subscriptionStatus === 'cancelled' || subscriptionStatus === 'abandoned' || subscriptionStatus === 'suspended') {
    return (
      <Banner style={{ background: '#f8d7da', color: '#721c24' }}>
        Tu suscripción no está activa ({subscriptionStatus}). No podrás operar hasta regularizar la situación.
        <a href="/plans" style={{ textDecoration: 'underline', color: '#721c24', marginLeft: 6 }}>Ir a Planes</a>
      </Banner>
    );
  }

  if (subscriptionStatus === 'past_due') {
    if (graceUntil) {
      const dleft = daysLeft(graceUntil);
      return (
        <Banner style={{ background: '#fff3cd', color: '#856404' }}>
          Pago pendiente. Tenés {isNaN(dleft) ? 'pocos' : dleft} día(s) de gracia para regularizar antes de la suspensión. {currentPeriodEnd ? `(Período vencido el ${new Date(currentPeriodEnd).toLocaleDateString()})` : ''}
        </Banner>
      );
    }
    return (
      <Banner style={{ background: '#f8d7da', color: '#721c24' }}>
        Pago pendiente y gracia vencida. Acceso limitado. Por favor, regularizá tu suscripción para continuar.
      </Banner>
    );
  }

  // URGENT BANNER: Check for 1 day or less remaining for ANY status (not just trial)
  const expiry = currentPeriodEnd || subscriptionExpiresAt || subscriptionExpiry;
  const dleft = expiry ? daysLeft(expiry) : null;

  if (dleft !== null && dleft <= 3) {
    const isExpired = dleft < 0;
    const daysOverdue = Math.abs(dleft);
    return (
      <div style={{
        background: 'linear-gradient(90deg, #dc2626 0%, #991b1b 100%)',
        color: '#fff',
        padding: '14px 20px',
        borderBottom: '3px solid #7f1d1d',
        boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
        animation: 'pulse 2s ease-in-out infinite'
      }}>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.85; }
          }
        `}</style>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 28 }}>🚨</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>
                {isExpired
                  ? `¡SUSCRIPCIÓN VENCIDA! (hace ${daysOverdue} día${daysOverdue > 1 ? 's' : ''})`
                  : `¡URGENTE! Tu suscripción vence ${dleft === 0 ? 'HOY' : dleft === 1 ? 'MAÑANA' : `en ${dleft} días`}`
                }
              </div>
              <div style={{ fontSize: 13, opacity: 0.95 }}>
                {isExpired && daysOverdue >= 10
                  ? '⛔ VENTAS BLOQUEADAS - Renovᡠpara reactivar'
                  : isExpired && daysOverdue >= 5
                    ? '🚫 AFIP BLOQUEADO - Renovᡠpara reactivar facturación'
                    : 'Renovᡠahora para evitar el bloqueo de funciones (AFIP en 5 días, ventas en 10 días)'
                }
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/plans')}
            style={{
              background: '#fff',
              color: isExpired ? '#991b1b' : '#dc2626',
              padding: '12px 24px',
              fontWeight: 700,
              fontSize: 15,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          >
            💳 RENOVAR AHORA
          </button>
        </div>
      </div>
    );
  }

  if (subscriptionStatus === 'trial') {
    // For trial status, prioritize currentPeriodEnd (updated by admin console)
    const expiry = currentPeriodEnd || subscriptionExpiresAt || subscriptionExpiry;
    const dleft = expiry ? daysLeft(expiry) : null;

    // Unificar la barra de prueba para TODAS las rutas, no solo Dashboard
    return (
      <div style={{
        background: '#f8fafc',
        color: '#1f2937',
        padding: '6px 10px',
        borderBottom: '1px solid #e5e7eb',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          {/* Izquierda: badge de prueba + actualizar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span
              style={{
                background: '#fff7ed',
                color: '#7c2d12',
                border: '1px solid #fbd38d',
                padding: '6px 10px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: 'normal',
                maxWidth: '100%',
              }}
              title={`Estás en periodo de prueba${expiry ? `: vence el ${fmtDate(expiry)}` : ''}${dleft !== null ? ` (${dleft} día(s) restantes)` : ''}`}
            >
              Estás en periodo de prueba{expiry ? `: vence el ${fmtDate(expiry)}` : ''}{dleft !== null ? ` (${dleft} día(s) restantes)` : ''}
            </span>
            <button
              onClick={() => navigate('/plans')}
              style={{ padding: '4px 8px', fontSize: 12, cursor: 'pointer', borderRadius: 6, border: '1px solid #e2e8f0', background: '#ffffff', flexShrink: 0 }}
            >
              Actualizar Plan
            </button>
          </div>
          {/* Derecha: info negocio y usuario */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 14, height: 14, background: '#60a5fa', display: 'inline-block', borderRadius: 2, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)', flexShrink: 0 }} />
              <div style={{ lineHeight: 1.1 }}>
                <div style={{ fontWeight: 700 }}>{razonSocial || '—'}</div>
                <div style={{ fontSize: 12, color: '#4b5563' }}>{cuit ? `CUIT: ${cuit}` : 'CUIT: —'}{tipoResp ? ` - ${tipoResp}` : ''}</div>
              </div>
            </div>
            <div style={{ width: 1, alignSelf: 'stretch', background: '#e5e7eb' }} />
            <div style={{ fontSize: 14, color: '#111827' }}>👤 {user?.name || user?.username || 'Usuario'}</div>
          </div>
        </div>
      </div>
    );
  }

  if (subscriptionStatus === 'active') {
    // For active status, prioritize currentPeriodEnd (updated by admin console)
    const end = currentPeriodEnd || tenant.subscriptionExpiresAt || subscriptionExpiry;
    const dleft = end ? daysLeft(end) : NaN;
    const when = end ? ` · Vence el ${fmtDate(end)}` : '';
    const daysText = !isNaN(dleft) ? (dleft >= 0 ? ` · ${dleft} día(s) restantes` : ` · Vencido hace ${Math.abs(dleft)} día(s)`) : '';
    const auto = autoRenew ? ' · Auto-renovar: sí' : ' · Auto-renovar: no';

    return (
      <div style={{
        background: '#e7f7ee',
        color: '#117a42',
        padding: '6px 10px',
        borderBottom: '1px solid #d1e7dd',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          {/* Izquierda: estado de suscripción + actualizar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span
              style={{
                background: '#d1e7dd',
                color: '#0f5132',
                border: '1px solid #badbcc',
                padding: '6px 10px',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: 'normal',
                maxWidth: '100%',
              }}
              title={`Activo: suscripto${when}${daysText}${auto}`}
            >
              Activo: suscripto{when}{daysText}{auto}
            </span>
            <button
              onClick={() => navigate('/plans')}
              style={{ padding: '4px 8px', fontSize: 12, cursor: 'pointer', borderRadius: 6, border: '1px solid #badbcc', background: '#ffffff', flexShrink: 0 }}
            >
              Actualizar Plan
            </button>
          </div>
          {/* Derecha: info negocio y usuario */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 14, height: 14, background: '#60a5fa', display: 'inline-block', borderRadius: 2, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)', flexShrink: 0 }} />
              <div style={{ lineHeight: 1.1 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{razonSocial || '—'}</div>
                <div style={{ fontSize: 11, color: '#0f5132' }}>{cuit ? `CUIT: ${cuit}` : 'CUIT: —'}{tipoResp ? ` - ${tipoResp}` : ''}</div>
              </div>
            </div>
            <div style={{ width: 1, alignSelf: 'stretch', background: '#badbcc' }} />
            <div style={{ fontSize: 14, color: '#0f5132', fontWeight: 600 }}>👤 {user?.name || user?.username || 'Usuario'}</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SubscriptionBanner;
